import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import logger from '../utils/logger';
import { generateLeaseKey } from '../utils/leaseKeyUtils';

class PDFReportService {
  constructor() {
    this.doc = null;
    this.pageWidth = 8.5; // Letter size width in inches
    this.pageHeight = 11; // Letter size height in inches
    this.margin = 0.5; // Half inch margins
    this.contentWidth = this.pageWidth - (2 * this.margin);
    this.contentHeight = this.pageHeight - (2 * this.margin);
    this.isCancelled = false;
  }

  // Initialize new PDF document
  initDocument() {
    this.doc = new jsPDF({
      orientation: 'portrait',
      unit: 'in',
      format: 'letter'
    });
  }

  // Add title page
  async addTitlePage(searchData) {
    if (!this.doc) this.initDocument();

    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Add the long logo image
    try {
      // Load the logo image
      const logoImg = new Image();
      logoImg.src = '/long_logo.png';
      
      // Wait for image to load
      await new Promise((resolve, reject) => {
        logoImg.onload = resolve;
        logoImg.onerror = reject;
        // Timeout after 2 seconds
        setTimeout(() => reject(new Error('Logo load timeout')), 2000);
      });
      
      // Calculate logo dimensions (reasonable size for top-left placement)
      const logoWidth = 2.0; // Appropriate size for top-left corner
      const logoHeight = (logoImg.height * logoWidth) / logoImg.width;
      
      // Position logo at top-left corner
      const logoX = 0.5; // Left margin
      const logoY = 0.5; // Top margin
      
      // Add the logo image to the PDF
      this.doc.addImage(logoImg, 'PNG', logoX, logoY, logoWidth, logoHeight);
    } catch (error) {
      // Fallback to text if logo fails to load
      this.doc.setTextColor(255, 255, 255);
      this.doc.setFontSize(24);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('Diligence Desk', 0.6, 1.1);
    }

    // Main title - centered
    this.doc.setTextColor(28, 100, 140); // #1c648c converted to RGB
    this.doc.setFontSize(36);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Desktop Diligence', this.pageWidth / 2, 3.5, { align: 'center' });
    //this.doc.text('Report', this.pageWidth / 2, 3.0, { align: 'center' });

    // Subtitle - centered
    this.doc.setFontSize(18);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(100, 100, 100);
    this.doc.text('Environmental and Regulatory', this.pageWidth / 2, 4.2, { align: 'center' });
    this.doc.text('Screening Report', this.pageWidth / 2, 4.6, { align: 'center' });

    // Report details - centered
    this.doc.setFontSize(14);
    this.doc.setTextColor(50, 50, 50);
    
    // User information
    const userEmail = searchData?.user?.email || searchData?.searchParams?.userEmail || 'Unknown User';
    this.doc.text(`User: ${userEmail}`, this.pageWidth / 2, 5.6, { align: 'center' });
    
    // Generation date
    this.doc.text(`Generated on: ${currentDate}`, this.pageWidth / 2, 5.9, { align: 'center' });

    // Add some space
    this.doc.text('', this.pageWidth / 2, 5.0, { align: 'center' });
    this.doc.text('', this.pageWidth / 2, 5.2, { align: 'center' });

    // Footer - centered
    this.addFooter(searchData);
  }

  // When map filter is active, chapter content is gated on filtered row sets (matches on-screen tabs)
  getChapterSearchData(searchData) {
    if (searchData?.filter?.isActive && searchData.filter.data) {
      return {
        ...searchData,
        data: searchData.filter.data,
        counts: searchData.filter.counts || searchData.counts,
      };
    }
    return searchData;
  }

  getTableRecordCount(dataBundle, tabName) {
    const key = String(tabName || '').toLowerCase();
    if (dataBundle?.counts?.[key] != null && dataBundle.counts[key] !== '') {
      const fromCounts = Number(dataBundle.counts[key]);
      if (!Number.isNaN(fromCounts)) return fromCounts;
    }
    const rows = dataBundle?.data?.[key];
    return Array.isArray(rows) ? rows.length : 0;
  }

  // Section title with underline (Report Contents style)
  drawSectionTitle(title, y) {
    this.doc.setTextColor(28, 100, 140);
    this.doc.setFontSize(14);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(title, 0.6, y);
    this.doc.setDrawColor(28, 100, 140);
    this.doc.setLineWidth(0.01);
    this.doc.line(0.6, y + 0.05, this.pageWidth - 0.6, y + 0.05);
    return y + 0.3;
  }

  // Ensure Y stays on page for Contents; start a new Contents-continued page if needed
  async ensureContentsSpace(y, needed = 0.4) {
    const maxY = this.pageHeight - 1.0;
    if (y + needed <= maxY) return y;
    this.addNewPage();
    const pageNumber = this.getCurrentPageNumber();
    await this.addPageHeader('Report Contents - Continued', pageNumber);
    return 1.2;
  }

  // Add Report Contents Page - Formal Design
  async addReportContentsPage(searchData) {
    if (!this.doc) return;

    // Add new page
    this.doc.addPage();
    
    // Add standard page header
    const pageNumber = this.getCurrentPageNumber();
    await this.addPageHeader('Report Contents', pageNumber);

    let currentY = 1.2;
    const lineSpacing = 0.22;
    const textX = this.margin + 0.2;
    const textMaxWidth = this.contentWidth - 0.4;

    // Report Information
    currentY = this.drawSectionTitle('Report Information', currentY);

    const state = searchData?.searchParams?.state || 'ALL STATES';
    this.addText(`Geographic Scope: ${state}`, textX, currentY, {
      fontSize: 10,
      fontStyle: 'normal',
      maxWidth: textMaxWidth
    });
    currentY += lineSpacing;

    const reportDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    this.addText(`Report Date: ${reportDate}`, textX, currentY, {
      fontSize: 10,
      fontStyle: 'normal',
      maxWidth: textMaxWidth
    });
    currentY += lineSpacing;

    if (searchData?.filter?.isActive) {
      const filterSummary = this.formatFilterSummary(searchData);
      this.addText(`Map Filter: Active — ${filterSummary}`, textX, currentY, {
        fontSize: 10,
        fontStyle: 'normal',
        maxWidth: textMaxWidth
      });
      currentY += lineSpacing;
    }

    // Search Parameters
    currentY += 0.35;
    currentY = await this.ensureContentsSpace(currentY, 0.8);
    currentY = this.drawSectionTitle('Search Parameters', currentY);

    const paramLines = this.buildSearchParameterLines(searchData);
    for (const line of paramLines) {
      const height = this.calculateTextHeight(line, textMaxWidth, 10);
      currentY = await this.ensureContentsSpace(currentY, height + 0.05);
      this.addText(line, textX, currentY, {
        fontSize: 10,
        fontStyle: 'normal',
        maxWidth: textMaxWidth
      });
      currentY += Math.max(lineSpacing, height + 0.04);
    }

    // Data Tables Included (full search)
    currentY += 0.35;
    currentY = await this.ensureContentsSpace(currentY, 2.5);
    currentY = this.drawSectionTitle('Data Tables Included', currentY);
    currentY += 0.25;
    const tableWidth = 7.0;
    const tableX = 0.7;
    currentY = this.drawFormalDataGrid(searchData, tableX, currentY, tableWidth, {
      data: searchData?.data,
      counts: searchData?.counts,
    });

    // Filtered Data Tables (map filter only)
    if (searchData?.filter?.isActive && searchData.filter.data) {
      currentY += 0.55;
      currentY = await this.ensureContentsSpace(currentY, 2.8);
      currentY = this.drawSectionTitle('Filtered Data Tables', currentY);

      const filterSummary = this.formatFilterSummary(searchData);
      this.addText(
        `Record counts after map filter (${filterSummary}). Analytics chapters below reflect this subset.`,
        textX,
        currentY,
        { fontSize: 10, fontStyle: 'normal', maxWidth: textMaxWidth }
      );
      currentY += lineSpacing + 0.2;

      currentY = this.drawFormalDataGrid(searchData, tableX, currentY, tableWidth, {
        data: searchData.filter.data,
        counts: searchData.filter.counts,
      });
    }
  }

  formatFilterSummary(searchData) {
    const keys = searchData?.filter?.selectedLeaseKeys || [];
    const leaseCount = keys.length;
    const keySet = new Set(keys);
    const counties = new Set();

    (searchData?.data?.wells || []).forEach((well) => {
      const district = well['District'] || well.district || '00';
      const leaseNumber = well['Lease Number'] || well.lease_number || null;
      const leaseName = well['Lease Name'] || well.lease_name || null;
      const county = well['County'] || well.county || null;
      const leaseKey = generateLeaseKey(district, leaseNumber, leaseName);
      if (keySet.has(leaseKey) && county) {
        counties.add(String(county).trim());
      }
    });

    const countyCount = counties.size;
    const countyLabel = countyCount === 1 ? 'County' : 'Counties';
    const leaseLabel = leaseCount === 1 ? 'Lease' : 'Leases';
    return `${countyCount} ${countyLabel}, ${leaseCount} ${leaseLabel}`;
  }

  formatParamList(value, maxItems = 8) {
    if (value == null || value === '') return null;
    const items = String(value)
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    if (items.length === 0) return null;
    if (items.length <= maxItems) return items.join(', ');
    const shown = items.slice(0, maxItems).join(', ');
    return `${shown} (+${items.length - maxItems} more)`;
  }

  formatDateRangeLabel(start, end) {
    const s = start && String(start).trim() ? String(start).trim() : null;
    const e = end && String(end).trim() ? String(end).trim() : null;
    if (!s && !e) return null;
    if (s && e) return `${s} – ${e}`;
    if (s) return `From ${s}`;
    return `Through ${e}`;
  }

  buildSearchParameterLines(searchData) {
    const params = searchData?.searchParams || {};
    const lines = [];

    const pushField = (label, raw) => {
      const formatted = this.formatParamList(raw);
      if (formatted) lines.push(`${label}: ${formatted}`);
    };

    pushField('Operator Name', params.operator_name);
    pushField('Operator Number', params.operator_number);
    pushField('Lease Name', params.lease_name);
    pushField('Lease Number', params.lease_number);
    pushField('County', params.county);
    pushField('Well API Number', params.api_number);

    const tables = [];
    if (params.search_wells) tables.push('Wells');
    if (params.search_production) tables.push('Production');
    if (params.search_inspections) tables.push('Inspections');
    if (params.search_violations) tables.push('Violations');
    if (params.search_severances) tables.push('Severances');
    if (params.search_spills) tables.push('Spills');
    lines.push(`Tables Searched: ${tables.length ? tables.join(', ') : 'None'}`);

    const activityRange = this.formatDateRangeLabel(
      searchData?.activityDateFilters?.start_date,
      searchData?.activityDateFilters?.end_date
    );
    lines.push(
      activityRange
        ? `Activity Date Range: ${activityRange}`
        : 'Activity Date Range: All available history'
    );

    const wellRange = this.formatDateRangeLabel(
      searchData?.wellDateFilters?.start_date,
      searchData?.wellDateFilters?.end_date
    );
    if (wellRange) {
      lines.push(`Well Date Range: ${wellRange}`);
    }

    if (lines.length === 0) {
      lines.push('No search criteria specified');
    }

    return lines;
  }

  // Draw formal data grid for Report Contents page
  // dataOverride: { data, counts } — full search or filtered subset
  drawFormalDataGrid(searchData, x, y, width, dataOverride = null) {
    const bundle = {
      data: dataOverride?.data ?? searchData?.data,
      counts: dataOverride?.counts ?? searchData?.counts,
    };
    const allTabs = this.getAllTabs(searchData);
    const itemHeight = 0.3; // Reduced row height (was 0.4)
    const itemSpacing = 0.05;
    
    // Filter to only include tables that were searched
    const searchedTabs = allTabs.filter(tab => this.wasDataTypeSearched(tab, searchData));
    
    // Table header (default text color)
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(0, 0, 0); // Default black text
    this.doc.text('Data Table', x + 0.1, y - 0.1);
    this.doc.text('Source', x + 1.5, y - 0.1); // Source column with more space
    
    // Records header (right-aligned)
    const matchedRecordsText = 'Records';
    const matchedRecordsWidth = this.doc.getTextWidth(matchedRecordsText);
    this.doc.text(matchedRecordsText, x + width - 1.8 - matchedRecordsWidth, y - 0.1);
    
    this.doc.text('Status', x + width - 0.8, y - 0.1);
    
    // Header underline (default black color) - positioned between header and first row
    this.doc.setDrawColor(0, 0, 0); // Default black color
    this.doc.setLineWidth(0.01);
    const underlineY = y + 0.02; // Position in middle between header and first row text
    this.doc.line(x, underlineY, x + width, underlineY);
    
    searchedTabs.forEach((tab, index) => {
      const itemY = y + (index * (itemHeight + itemSpacing));
      const recordCount = this.getTableRecordCount(bundle, tab);
      const wasSearched = this.wasDataTypeSearched(tab, searchData);
      
      // Data type name (10pt font size - same as regular text)
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(0, 0, 0); // Default black text
      this.doc.text(this.getFormalTableName(tab), x + 0.1, itemY + 0.25);
      
      // Source (10pt font size - same as regular text)
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(0, 0, 0); // Default black text
      this.doc.text(this.getTableSource(tab), x + 1.5, itemY + 0.25); // Match header position
      
      // Record count (10pt font size - same as regular text, right-aligned)
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(0, 0, 0); // Default black text
      const recordCountText = recordCount.toLocaleString();
      const recordCountWidth = this.doc.getTextWidth(recordCountText);
      this.doc.text(recordCountText, x + width - 1.8 - recordCountWidth, itemY + 0.25); // Right-aligned
      
      // Status indicator (10pt font size - same as regular text)
      const statusX = x + width - 0.8;
      let statusText;
      
      if (wasSearched) {
        if (recordCount > 0) {
          statusText = 'Included';
        } else {
          statusText = 'No Data';
        }
      } else {
        statusText = 'Excluded';
      }
      
      // Status text (10pt font size - same as regular text)
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(0, 0, 0); // Default black text
      this.doc.text(statusText, statusX, itemY + 0.25);
    });

    const rows = searchedTabs.length;
    return y + rows * (itemHeight + itemSpacing) + 0.1;
  }

  // Get formal table name for display
  getFormalTableName(tabName) {
    const formalNames = {
      'Wells': 'Wells',
      'Production': 'Production',
      'Inspections': 'Inspections',
      'Violations': 'Violations',
      'Severances': 'Severances',
      'Spills': 'Spills'
    };
    return formalNames[tabName];
  }

  // Get table source information
  getTableSource(tabName) {
    const sources = {
      'Wells': 'Texas Production',
      'Production': 'Texas Production',
      'Inspections': 'Texas Production',
      'Violations': 'Texas Production',
      'Severances': 'Texas Production',
      'Spills': 'Texas Production'
    };
    return sources[tabName] || 'Unknown';
  }

  // Draw modern data grid (legacy - kept for compatibility)
  drawModernDataGrid(searchData, x, y, width) {
    const allTabs = this.getAllTabs(searchData);
    const availableTabs = this.getAvailableTabs(searchData);
    const itemHeight = 0.35;
    const itemSpacing = 0.0;
    
    // Filter to only include tables that were searched
    const searchedTabs = allTabs.filter(tab => this.wasDataTypeSearched(tab, searchData));
    
    searchedTabs.forEach((tab, index) => {
      const itemY = y + (index * (itemHeight + itemSpacing));
      const hasData = availableTabs.includes(tab);
      const recordCount = hasData ? (searchData?.data?.[tab.toLowerCase()]?.length || 0) : 0;
      const wasSearched = this.wasDataTypeSearched(tab, searchData);
      
      // Item background (alternating)
      if (index % 2 === 0) {
        this.doc.setFillColor(255, 255, 255);
      } else {
        this.doc.setFillColor(248, 249, 250);
      }
      this.doc.rect(x, itemY, width, itemHeight, 'F');
      
      // Item border
      this.doc.setDrawColor(230, 230, 230);
      this.doc.setLineWidth(0.005);
      this.doc.rect(x, itemY, width, itemHeight);
      
      // Data type name
      this.doc.setFontSize(11);
      this.doc.setFont('helvetica', 'bold');
      this.doc.setTextColor(50, 50, 50);
      this.doc.text(tab, x + 0.1, itemY + 0.225);
      
      // Search status indicator
      const statusX = x + width - 1.3;
      let circleColor, textColor, searchText;
      
      if (wasSearched) {
        if (recordCount > 0) {
          // Green circle - searched with data available
          circleColor = [76, 175, 80];
          textColor = [76, 175, 80];
          searchText = 'Data available';
        } else {
          // Orange circle - searched but no records found
          circleColor = [255, 152, 0];
          textColor = [255, 152, 0];
          searchText = 'No records found';
        }
      } else {
        // Gray circle - not searched
        circleColor = [200, 200, 200];
        textColor = [150, 150, 150];
        searchText = 'Not in search';
      }
      
      // Draw circle
      this.doc.setFillColor(circleColor[0], circleColor[1], circleColor[2]);
      this.doc.circle(statusX, itemY + 0.175, 0.08, 'F');
      
      // Search status text
      this.doc.setFontSize(9);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      this.doc.text(searchText, statusX + 0.15, itemY + 0.225);
    });
  }

  // Check if a data type was included in search parameters
  wasDataTypeSearched(tabName, searchData) {
    if (!searchData?.searchParams) return false;
    
    const { searchParams } = searchData;
    const tabKey = tabName.toLowerCase();
    
    // Map tab names to their corresponding search parameter flags
    const searchFlagMap = {
      'wells': 'search_wells',
      'production': 'search_production',
      'inspections': 'search_inspections',
      'violations': 'search_violations',
      'severances': 'search_severances',
      'spills': 'search_spills'
    };
    
    const searchFlag = searchFlagMap[tabKey];
    return searchFlag ? !!searchParams[searchFlag] : false;
  }

  // Draw a table with the given data
  drawTable(data, x, y, width, rowHeight) {
    if (!this.doc || !data || data.length === 0) return;

    const colWidth = width / data[0].length;
    let currentY = y;

    data.forEach((row, rowIndex) => {
      // Draw row background
      if (rowIndex === 0) {
        // Header row
        this.doc.setFillColor(28, 100, 140); // #1c648c converted to RGB
        this.doc.rect(x, currentY - 0.1, width, rowHeight + 0.1, 'F');
        this.doc.setTextColor(255, 255, 255);
        this.doc.setFont('helvetica', 'bold');
        this.doc.setFontSize(10);
      } else {
        // Data rows - no fill, just text
        this.doc.setTextColor(0, 0, 0);
        this.doc.setFont('helvetica', 'normal');
        this.doc.setFontSize(9);
      }

      // Draw cell content
      row.forEach((cell, colIndex) => {
        const cellX = x + (colIndex * colWidth) + 0.05;
        const cellWidth = colWidth - 0.1; // Account for padding
        
        if (rowIndex === 0) {
          // Center header text both horizontally and vertically within each cell
          this.doc.text(cell.toString(), cellX + cellWidth/2, currentY + 0.15, { align: 'center' });
        } else {
          // Left-align data text
          this.doc.text(cell.toString(), cellX, currentY + 0.05);
        }
      });

      // Move to next row - account for header padding
      if (rowIndex === 0) {
        // Header row extends rowHeight + 0.1, so move by that amount
        currentY += rowHeight + 0.1;
      } else {
        // Data rows just move by rowHeight
        currentY += rowHeight;
      }
    });

    // Draw table border and column lines
    this.doc.setDrawColor(200, 200, 200);
    this.doc.setLineWidth(0.01);
    
    // Draw outer border
    this.doc.rect(x, y - 0.1, width, currentY - y + 0.1);
    
    // Draw vertical lines between columns
    for (let i = 1; i < data[0].length; i++) {
      const lineX = x + (i * colWidth);
      this.doc.line(lineX, y - 0.1, lineX, currentY - 0.1);
    }
    
    // Draw horizontal lines between rows (not after the last row)
    let lineY = y - 0.1;
    for (let i = 0; i < data.length - 1; i++) {
      if (i === 0) {
        // After header row
        lineY += rowHeight + 0.1;
      } else {
        // After each data row
        lineY += rowHeight;
      }
      this.doc.line(x, lineY, x + width, lineY);
    }
  }

  // Format search criteria for display (legacy helper; Contents uses buildSearchParameterLines)
  formatSearchCriteria(searchParams) {
    return this.buildSearchParameterLines({ searchParams }).join('; ');
  }

  // Get all tabs (including those with no data)
  getAllTabs(searchData) {
    const tabLabels = {
      wells: 'Wells',
      production: 'Production',
      inspections: 'Inspections',
      violations: 'Violations',
      severances: 'Severances',
      spills: 'Spills'
    };

    return Object.values(tabLabels);
  }

  // Get available tabs based on search data (only those with data)
  getAvailableTabs(searchData) {
    const tabLabels = {
      wells: 'Wells',
      production: 'Production',
      inspections: 'Inspections',
      violations: 'Violations',
      severances: 'Severances',
      spills: 'Spills'
    };

    if (!searchData?.data) return Object.values(tabLabels);

    const availableTabs = [];
    Object.keys(tabLabels).forEach(tableType => {
      if (searchData.data[tableType]?.length > 0) {
        availableTabs.push(tabLabels[tableType]);
      }
    });

    return availableTabs;
  }

  // Add new page
  addNewPage() {
    if (!this.doc) this.initDocument();
    this.doc.addPage();
  }

  // Add footer to current page
  addFooter(searchData) {
    if (!this.doc) return;
    
    const userEmail = searchData?.user?.email || 'Unknown User';
    const footerText = `Diligence Desk Intelligence Platform | User: ${userEmail}`;
    
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal'); // Explicitly set normal font
    this.doc.setTextColor(150, 150, 150);
    this.doc.text(footerText, this.pageWidth / 2, 10.5, { align: 'center' });
  }

  // Add page header
  async addPageHeader(title, pageNumber) {
    if (!this.doc) return;

    // Header background
    this.doc.setFillColor(28, 100, 140); // #1c648c converted to RGB
    this.doc.rect(0.5, 0.5, this.contentWidth, 0.3, 'F');

    // Add shield logo to header
    try {
      // Load the shield logo image
      const shieldImg = new Image();
      shieldImg.src = '/shield_logo.png';
      
      // Wait for image to load
      await new Promise((resolve, reject) => {
        shieldImg.onload = resolve;
        shieldImg.onerror = reject;
        // Timeout after 1 second (shorter timeout for header)
        setTimeout(() => reject(new Error('Shield logo load timeout')), 1000);
      });
      
      // Calculate shield logo dimensions (slightly larger for header)
      const shieldWidth = 0.25; // Slightly larger size for better visibility
      const shieldHeight = (shieldImg.height * shieldWidth) / shieldImg.width;
      
      // Position shield logo at left side of header
      const shieldX = 0.6; // Left margin
      const shieldY = 0.5 + (0.3 - shieldHeight) / 2; // Center vertically in header
      
      // Add the shield logo image to the PDF
      this.doc.addImage(shieldImg, 'PNG', shieldX, shieldY, shieldWidth, shieldHeight);
      
      // Adjust title position to account for logo and center vertically
      this.doc.setTextColor(255, 255, 255);
      this.doc.setFontSize(14);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text(title, 0.95, 0.72); // Moved right and down for perfect vertical centering
    } catch (error) {
      // Fallback: no logo, use original title position
      this.doc.setTextColor(255, 255, 255);
      this.doc.setFontSize(14);
      this.doc.setFont('helvetica', 'bold');
      this.doc.text(title, 0.6, 0.72); // Vertically centered in header
    }

    // Page number
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(`Page ${pageNumber}`, this.pageWidth - 1.1, 0.7); // Vertically centered
  }

  // Add Report Card Analysis page content
  async addReportCardAnalysisPage(searchData) {
    if (!this.doc) return;

    let currentY = 1.2;

    // Switch to Report Card toggle to ensure we capture the right content
    await this.switchToReportCardToggle();

    // Add report card counts (e.g., "3 Counties | 47 Leases | 66 Wells") - styled like Findings
    const reportCardText = this.captureReportCardCounts(searchData);
    
    // Report Card title (styled like Findings)
    this.doc.setTextColor(28, 100, 140); // #1c648c converted to RGB
    this.doc.setFontSize(14);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(reportCardText, 0.6, currentY);

    // Underline (styled like Findings)
    this.doc.setDrawColor(28, 100, 140); // #1c648c converted to RGB
    this.doc.setLineWidth(0.01);
    this.doc.line(0.6, currentY + 0.05, this.pageWidth - 0.6, currentY + 0.05);
    
    currentY += 0.4;

    // Add data flow diagram
    currentY = await this.captureDataFlowDiagram(searchData, currentY);

    return currentY;
  }

  // Switch to Report Card toggle
  async switchToReportCardToggle() {
    // Step 1: First, switch to the Report Card tab in AnalyticsSection (Tab index 1)
    const analyticsTabs = document.querySelectorAll('[role="tab"]');
    
    for (const tab of analyticsTabs) {
      // Look for the Report Card tab - it should have value="1" or text "Report Card"
      const tabValue = tab.getAttribute('value');
      const tabText = tab.textContent?.trim();
      
      if (tabValue === '1' || tabText === 'Report Card') {
        tab.click();
        // Wait for the tab to switch and RelationalChart to render
        await new Promise(resolve => setTimeout(resolve, 500));
        break;
      }
    }
    
    // Step 2: Wait for RelationalChart to be visible/rendered
    await this.waitForReportCardTabToRender();
    
    // Step 3: Now switch to the Report Card view mode in RelationalChart
    // Try multiple selectors to find the Report Card view mode toggle button
    let reportCardViewToggleButton = document.querySelector('button[value="summary"]') || 
                                  document.querySelector('[role="button"][value="summary"]');
    
    // If not found, try looking for ToggleButton components with "Report Card" text
    if (!reportCardViewToggleButton) {
      const toggleButtons = document.querySelectorAll('[role="button"]');
      for (const toggleButton of toggleButtons) {
        const buttonText = toggleButton.textContent?.trim();
        const buttonValue = toggleButton.getAttribute('value');
        if ((buttonText === 'Report Card' || buttonValue === 'summary') && 
            toggleButton.closest('.MuiToggleButtonGroup-root')) {
          reportCardViewToggleButton = toggleButton;
          break;
        }
      }
    }
    
    if (reportCardViewToggleButton) {
      reportCardViewToggleButton.click();
      // Wait for the view mode to switch
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }

  // Smart waiting for Report Card tab to render (max 3 seconds)
  async waitForReportCardTabToRender() {
    return new Promise((resolve) => {
      const startTime = Date.now();
      const maxWaitTime = 3000; // 3 seconds max
      
      const checkReportCardTab = () => {
        const elapsed = Date.now() - startTime;
        
        // Timeout after 3 seconds
        if (elapsed >= maxWaitTime) {
          resolve();
          return;
        }
        
        // Check if RelationalChart is rendered (look for the view mode toggle buttons)
        const viewModeToggle = document.querySelector('.MuiToggleButtonGroup-root');
        const reportCardViewButton = document.querySelector('button[value="summary"]') || 
                                  Array.from(document.querySelectorAll('[role="button"]')).find(btn => 
                                    btn.textContent?.trim() === 'Report Card' && 
                                    btn.closest('.MuiToggleButtonGroup-root')
                                  );
        
        if (viewModeToggle && reportCardViewButton) {
          resolve(); // Report Card tab is ready!
        } else {
          setTimeout(checkReportCardTab, 100); // Check again in 100ms
        }
      };
      
      checkReportCardTab();
    });
  }

  // Capture report card counts text
  captureReportCardCounts(searchData) {
    if (!searchData?.data) return 'No data available';

    // Extract the actual text from the DOM - it's in a Typography with variant="body1", bold, primary color
    try {
      // Look for Typography elements with body1 variant (p tags or MuiTypography-body1 class)
      const summaryElements = document.querySelectorAll('p, [class*="MuiTypography-body1"]');
      for (const element of summaryElements) {
        const text = element.textContent?.trim();
        // Check if it matches the expected format: "X County/Counties | Y Lease/Leases | Z Well Record/Records | W Unique API/APIs | N Flag/Flags"
        if (text && 
            (text.includes('Count') || text.includes('Counties')) && 
            text.includes('Lease') && 
            (text.includes('Well Record') || text.includes('Wells')) && 
            text.includes('Unique API') && 
            text.includes('|')) {
          // Verify it's the right element by checking styles (bold and primary color)
          const styles = window.getComputedStyle(element);
          const fontWeight = styles.fontWeight;
          const isBold = parseInt(fontWeight) >= 600 || fontWeight === 'bold';
          
          if (isBold) {
            return text;
          }
        }
      }
    } catch (error) {
      logger.warn('Could not extract summary text from DOM:', error);
    }

    // If DOM extraction fails, return a fallback message
    logger.warn('Report Card text not found in DOM');
    return 'Report Card data not available';
  }

  // Capture data flow diagram
  async captureDataFlowDiagram(searchData, startY) {
    if (!this.doc) return startY;

    // Wait for the diagram element to appear with smart polling (max 3 seconds)
    const flowDiagramElement = await this.waitForReportCardDiagramToRender();
    
    if (!flowDiagramElement) {
      logger.warn('Report Card diagram element not found after waiting');
      return startY;
    }

    try {
      // Temporarily expand the flow diagram for better capture
      const originalStyles = await this.temporarilyExpandFlowDiagram(flowDiagramElement);
      
      // Wait for re-render
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Capture the flow diagram
      const canvas = await html2canvas(flowDiagramElement, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        width: flowDiagramElement.scrollWidth,
        height: flowDiagramElement.scrollHeight
      });

      // Restore original styles
      this.restoreFlowDiagramStyles(flowDiagramElement, originalStyles);

      // Add to PDF
      const imgData = canvas.toDataURL('image/jpeg', 0.8);
      const imgWidth = this.contentWidth - 0.4;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Limit height (increased for Summary page since it's the only content)
      const maxHeight = 6.0;
      let finalWidth = imgWidth;
      let finalHeight = imgHeight;

      if (imgHeight > maxHeight) {
        const scale = maxHeight / imgHeight;
        finalWidth = imgWidth * scale;
        finalHeight = maxHeight;
      }

      // Center horizontally
      const xPosition = (this.pageWidth - finalWidth) / 2;
      
      this.doc.addImage(imgData, 'JPEG', xPosition, startY, finalWidth, finalHeight);

      return startY + finalHeight + 0.3;
    } catch (error) {
      logger.error('Error capturing flow diagram:', error);
      return startY;
    }
  }

  // Smart waiting for Report Card diagram to render (max 3 seconds)
  async waitForReportCardDiagramToRender() {
    return new Promise((resolve) => {
      const startTime = Date.now();
      const maxWaitTime = 3000; // 3 seconds max
      
      const checkDiagram = () => {
        const elapsed = Date.now() - startTime;
        
        // Timeout after 3 seconds
        if (elapsed >= maxWaitTime) {
          // Try one final time to find the element
          const finalElement = this.findReportCardDiagramElement();
          resolve(finalElement);
          return;
        }
        
        // Try to find the diagram element
        const flowDiagramElement = this.findReportCardDiagramElement();
        
        if (flowDiagramElement) {
          // Verify it has content (Paper components)
          const papers = flowDiagramElement.querySelectorAll('.MuiPaper-root');
          if (papers.length >= 2) {
            resolve(flowDiagramElement); // Diagram is ready!
            return;
          }
        }
        
        setTimeout(checkDiagram, 100); // Check again in 100ms
      };
      
      checkDiagram();
    });
  }

  // Helper to find the report card diagram element
  findReportCardDiagramElement() {
    // First try: Look for the specific flow diagram container
    let flowDiagramElement = document.querySelector('[data-flow-diagram]');
    
    // Second try: Look for the Box containing the cards (more specific)
    if (!flowDiagramElement) {
      const boxes = document.querySelectorAll('.MuiBox-root');
      for (const box of boxes) {
        const style = window.getComputedStyle(box);
        if (style.display === 'flex' && 
            style.alignItems === 'center' && 
            style.flexWrap === 'wrap' &&
            box.querySelector('.MuiPaper-root')) {
          flowDiagramElement = box;
          break;
        }
      }
    }
    
    // Third try: Look for any container with Paper components (cards) and arrows
    if (!flowDiagramElement) {
      const containers = document.querySelectorAll('div');
      for (const container of containers) {
        const papers = container.querySelectorAll('.MuiPaper-root');
        const arrows = container.querySelectorAll('span');
        if (papers.length >= 2 && arrows.some(arrow => arrow.textContent === '→')) {
          flowDiagramElement = container;
          break;
        }
      }
    }
    
    return flowDiagramElement;
  }

  // Temporarily expand flow diagram for capture
  async temporarilyExpandFlowDiagram(element) {
    const originalStyles = {
      width: element.style.width,
      height: element.style.height,
      minWidth: element.style.minWidth,
      minHeight: element.style.minHeight,
      maxWidth: element.style.maxWidth,
      maxHeight: element.style.maxHeight,
      flex: element.style.flex
    };

    // Expand for better capture with specific aspect ratio (2.5:1)
    element.style.width = '800px';
    element.style.height = '320px'; // 800/2.5 = 320px
    element.style.minWidth = '800px';
    element.style.minHeight = '320px';
    element.style.maxWidth = 'none';
    element.style.maxHeight = 'none';
    element.style.flex = 'none';

    return originalStyles;
  }

  // Restore flow diagram styles
  restoreFlowDiagramStyles(element, originalStyles) {
    if (!element || !originalStyles) return;

    Object.keys(originalStyles).forEach(prop => {
      element.style[prop] = originalStyles[prop];
    });
  }

  // Add section title
  addSectionTitle(title, yPosition) {
    if (!this.doc) return;

    this.doc.setTextColor(28, 100, 140); // #1c648c converted to RGB
    this.doc.setFontSize(14);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(title, 0.6, yPosition);

    // Underline
    this.doc.setDrawColor(28, 100, 140); // #1c648c converted to RGB
    this.doc.setLineWidth(0.01);
    this.doc.line(0.6, yPosition + 0.05, this.pageWidth - 0.6, yPosition + 0.05);
  }

  // Add text content
  addText(text, x, y, options = {}) {
    if (!this.doc) return;

    const {
      fontSize = 10,
      fontStyle = 'normal',
      color = [0, 0, 0],
      align = 'left',
      maxWidth = this.contentWidth - 0.2
    } = options;

    this.doc.setFontSize(fontSize);
    this.doc.setFont('helvetica', fontStyle);
    this.doc.setTextColor(...color);
    
    if (maxWidth < this.contentWidth - 0.2) {
      this.doc.text(text, x, y, { align, maxWidth });
    } else {
      this.doc.text(text, x, y, { align });
    }
  }

  calculateTextHeight(text, maxWidth, fontSize) {
    if (!this.doc) return 0.2; // Default height if no doc

    // Set the font to calculate text dimensions
    this.doc.setFontSize(fontSize);
    this.doc.setFont('helvetica', 'normal');
    
    // Split text into lines based on maxWidth
    const lines = this.doc.splitTextToSize(text, maxWidth);
    // Convert font size from points to inches (72 points = 1 inch)
    const lineHeight = (fontSize / 72) * 1.2; // 1.2 is a reasonable line height multiplier
    
    return lines.length * lineHeight;
  }

  // Add table
  addTable(headers, data, startY, options = {}) {
    if (!this.doc) return startY;

    const {
      fontSize = 8,
      headerFontSize = 9,
      rowHeight = 0.25,
      cellPadding = 0.05,
      maxRows = 20
    } = options;

    const colWidths = this.calculateColumnWidths(headers.length);
    let currentY = startY;

    // Table header
    this.doc.setFillColor(240, 240, 240);
    this.doc.rect(0.6, currentY, this.contentWidth - 0.2, rowHeight, 'F');

    this.doc.setTextColor(0, 0, 0);
    this.doc.setFontSize(headerFontSize);
    this.doc.setFont('helvetica', 'bold');

    let xPos = 0.6 + cellPadding;
    headers.forEach((header, index) => {
      this.doc.text(header, xPos, currentY + rowHeight - cellPadding);
      xPos += colWidths[index];
    });

    currentY += rowHeight;

    // Table data
    this.doc.setFontSize(fontSize);
    this.doc.setFont('helvetica', 'normal');

    data.slice(0, maxRows).forEach((row, rowIndex) => {
      // Alternate row colors
      if (rowIndex % 2 === 0) {
        this.doc.setFillColor(250, 250, 250);
        this.doc.rect(0.6, currentY, this.contentWidth - 0.2, rowHeight, 'F');
      }

      xPos = 0.6 + cellPadding;
      row.forEach((cell, colIndex) => {
        const cellText = String(cell || '').substring(0, 30); // Truncate long text
        this.doc.text(cellText, xPos, currentY + rowHeight - cellPadding);
        xPos += colWidths[colIndex];
      });

      currentY += rowHeight;
    });

    if (data.length > maxRows) {
      this.doc.setTextColor(100, 100, 100);
      this.doc.setFontSize(8);
      this.doc.text(`... and ${data.length - maxRows} more rows`, 0.6, currentY + 0.1);
      currentY += 0.2;
    }

    return currentY + 0.1;
  }

  // Calculate column widths based on content
  calculateColumnWidths(numCols) {
    const totalWidth = this.contentWidth - 0.2;
    const colWidth = totalWidth / numCols;
    return new Array(numCols).fill(colWidth);
  }

  // Add chart from DOM element
  async addChartFromElement(chartElement, title, yPosition) {
    if (!this.doc) return yPosition + 2;

    this.addSectionTitle(title, yPosition);
    
    try {
      if (chartElement) {
        // Temporarily expand chart width for better mobile capture
        let originalStyles = await this.temporarilyExpandChartWidth(chartElement);
        
        // Wait for chart to re-render with expanded width
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Validate chart is properly expanded, retry if not
        let retryCount = 0;
        const maxRetries = 3;
        while (retryCount < maxRetries && !this.isChartProperlyExpanded(chartElement)) {
          // Restore and try again
          this.restoreChartWidth(chartElement, originalStyles);
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Re-expand
          const newOriginalStyles = await this.temporarilyExpandChartWidth(chartElement);
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          retryCount++;
          if (retryCount < maxRetries) {
            originalStyles = newOriginalStyles;
          }
        }
        
        // Capture the chart as an image
        const canvas = await html2canvas(chartElement, {
          backgroundColor: '#ffffff',
          scale: 2, // Higher resolution
          useCORS: true,
          allowTaint: true
        });
        
        // Restore original styles
        this.restoreChartWidth(chartElement, originalStyles);
        
        const imgData = canvas.toDataURL('image/jpeg');
        const imgWidth = this.contentWidth - 0.4; // Slightly smaller for centering
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        // Limit height to prevent overflow
        const maxHeight = 3.0;
        let finalWidth = imgWidth;
        let finalHeight = imgHeight;
        
        if (imgHeight > maxHeight) {
          const scale = maxHeight / imgHeight;
          finalWidth = imgWidth * scale;
          finalHeight = maxHeight;
        }
        
        // Center the chart horizontally
        const xPosition = (this.pageWidth - finalWidth) / 2;
        
        this.doc.addImage(imgData, 'PNG', xPosition, yPosition + 0.2, finalWidth, finalHeight);
        return yPosition + finalHeight + 0.5;
      }
    } catch (error) {
    }
    
    // No fallback - chart capture must succeed
    return yPosition;
  }

  // Temporarily expand chart width for better mobile capture
  async temporarilyExpandChartWidth(chartElement) {
    // Find the ResponsiveContainer parent
    const responsiveContainer = chartElement.closest('.recharts-responsive-container') || chartElement;
    const paperContainer = chartElement.closest('.MuiPaper-root');
    
    const originalStyles = {
      chart: {
        width: chartElement.style.width,
        height: chartElement.style.height,
        minWidth: chartElement.style.minWidth,
        maxWidth: chartElement.style.maxWidth,
        flex: chartElement.style.flex
      },
      responsive: {
        width: responsiveContainer.style.width,
        height: responsiveContainer.style.height,
        minWidth: responsiveContainer.style.minWidth,
        maxWidth: responsiveContainer.style.maxWidth,
        flex: responsiveContainer.style.flex,
        position: responsiveContainer.style.position,
        left: responsiveContainer.style.left,
        top: responsiveContainer.style.top,
        zIndex: responsiveContainer.style.zIndex
      },
      paper: paperContainer ? {
        width: paperContainer.style.width,
        height: paperContainer.style.height,
        minWidth: paperContainer.style.minWidth,
        maxWidth: paperContainer.style.maxWidth,
        flex: paperContainer.style.flex,
        position: paperContainer.style.position,
        left: paperContainer.style.left,
        top: paperContainer.style.top,
        zIndex: paperContainer.style.zIndex
      } : null
    };

    // Expand the paper container first (if it exists)
    if (paperContainer) {
      paperContainer.style.width = '900px';
      paperContainer.style.minWidth = '900px';
      paperContainer.style.maxWidth = 'none';
      paperContainer.style.flex = 'none';
      paperContainer.style.position = 'fixed';
      paperContainer.style.left = '-9999px'; // Move off-screen
      paperContainer.style.top = '0';
      paperContainer.style.transform = 'none';
      paperContainer.style.zIndex = '9998'; // Behind the loading mask
    }

    // Expand the ResponsiveContainer
    responsiveContainer.style.width = '800px';
    responsiveContainer.style.height = '400px';
    responsiveContainer.style.minWidth = '800px';
    responsiveContainer.style.maxWidth = 'none';
    responsiveContainer.style.flex = 'none';
    if (!paperContainer) {
      responsiveContainer.style.position = 'fixed';
      responsiveContainer.style.left = '-9999px'; // Move off-screen
      responsiveContainer.style.top = '0';
      responsiveContainer.style.transform = 'none';
      responsiveContainer.style.zIndex = '9998'; // Behind the loading mask
    }

    // Expand the chart element
    chartElement.style.width = '800px';
    chartElement.style.height = '400px';
    chartElement.style.minWidth = '800px';
    chartElement.style.maxWidth = 'none';
    chartElement.style.flex = 'none';

    // Force multiple resize events to trigger chart re-render
    window.dispatchEvent(new Event('resize'));
    await new Promise(resolve => setTimeout(resolve, 300));
    window.dispatchEvent(new Event('resize'));
    await new Promise(resolve => setTimeout(resolve, 300));
    window.dispatchEvent(new Event('resize'));

    return originalStyles;
  }

  // Restore original chart width
  restoreChartWidth(chartElement, originalStyles) {
    // Find the same containers
    const responsiveContainer = chartElement.closest('.recharts-responsive-container') || chartElement;
    const paperContainer = chartElement.closest('.MuiPaper-root');

    // Restore chart element styles
    Object.keys(originalStyles.chart).forEach(prop => {
      chartElement.style[prop] = originalStyles.chart[prop];
    });

    // Restore ResponsiveContainer styles
    Object.keys(originalStyles.responsive).forEach(prop => {
      responsiveContainer.style[prop] = originalStyles.responsive[prop];
    });

    // Restore paper container styles (if it exists)
    if (paperContainer && originalStyles.paper) {
      Object.keys(originalStyles.paper).forEach(prop => {
        paperContainer.style[prop] = originalStyles.paper[prop];
      });
    }

    // Reset transforms
    chartElement.style.transform = '';
    responsiveContainer.style.transform = '';
    if (paperContainer) {
      paperContainer.style.transform = '';
    }

    // Force resize events to restore layout
    window.dispatchEvent(new Event('resize'));
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
  }

  // Add chart placeholder (fallback)

  // Add actual charts from DOM elements
  async addActualCharts(tabName, chartElements, yPosition) {
    if (!this.doc) return yPosition + 1;

    let currentY = yPosition;
    
    try {
      // First, try to switch to the correct tab to capture its charts
      await this.switchToTab(tabName);
      
      // Wait for charts to render (smart waiting, max 3 seconds)
      await this.waitForChartsToRender();
      
      // Find all ResponsiveContainer elements (Recharts containers) in the current tab
      const chartContainers = document.querySelectorAll('.recharts-wrapper');
      
      if (chartContainers.length > 0) {
        // Add each visible chart
        for (let i = 0; i < Math.min(chartContainers.length, 3); i++) {
          const chartElement = chartContainers[i];
          const chartTitle = this.getChartTitleForIndex(tabName, i);
          
          currentY = await this.addChartFromElement(chartElement, chartTitle, currentY);
        }
      } else {
        // No charts found - skip this section
      }
    } catch (error) {
      // No fallback - charts must be captured from DOM
    }

    return currentY;
  }

  // Switch to the specified tab to capture its charts
  async switchToTab(tabName) {
    try {
      // Find the tab button for the specified tab
      const tabButtons = document.querySelectorAll('[role="tab"]');
      const tabLabels = {
        'Wells': 'Wells',
        'Production': 'Production',
        'Inspections': 'Inspections',
        'Violations': 'Violations',
        'Severances': 'Severances',
        'Spills': 'Spills'
      };
      
      const targetTabLabel = tabLabels[tabName];
      if (targetTabLabel) {
        for (const tabButton of tabButtons) {
          if (tabButton.textContent.trim() === targetTabLabel) {
            tabButton.click();
            break;
          }
        }
      }
    } catch (error) {
    }
  }

  // Get chart title for index
  getChartTitleForIndex(tabName, index) {
    // Try to extract title from the chart container
    const chartContainers = document.querySelectorAll('.recharts-wrapper');
    if (chartContainers[index]) {
      const chartContainer = chartContainers[index];
      
      // Look for the card container that wraps the chart
      const cardContainer = chartContainer.closest('.MuiPaper-root') || 
                           chartContainer.closest('.MuiCard-root') ||
                           chartContainer.closest('[class*="Card"]') ||
                           chartContainer.closest('[class*="card"]');
      
      if (cardContainer) {
        // Look for title elements in the card header
        const titleElement = cardContainer.querySelector('.MuiTypography-h6') ||
                           cardContainer.querySelector('.MuiTypography-h5') ||
                           cardContainer.querySelector('.MuiTypography-h4') ||
                           cardContainer.querySelector('h6') ||
                           cardContainer.querySelector('h5') ||
                           cardContainer.querySelector('h4') ||
                           cardContainer.querySelector('h3') ||
                           cardContainer.querySelector('[class*="title"]') ||
                           cardContainer.querySelector('[class*="Title"]') ||
                           cardContainer.querySelector('.chart-title') ||
                           cardContainer.querySelector('[data-testid="chart-title"]');
        
        if (titleElement && titleElement.textContent) {
          return titleElement.textContent.trim();
        }
      }
      
      // Fallback: Look for various title elements that might be in the chart itself
      const titleElement = chartContainer.querySelector('.recharts-legend-item-text') || 
                          chartContainer.querySelector('.recharts-text') ||
                          chartContainer.querySelector('[data-testid="chart-title"]') ||
                          chartContainer.querySelector('.chart-title') ||
                          chartContainer.querySelector('h3') ||
                          chartContainer.querySelector('h4') ||
                          chartContainer.querySelector('h5') ||
                          chartContainer.querySelector('h6');
      
      if (titleElement && titleElement.textContent) {
        return titleElement.textContent.trim();
      }
      
      // Look for axis labels that might give us a clue
      const axisLabels = chartContainer.querySelectorAll('.recharts-cartesian-axis-tick-value');
      if (axisLabels.length > 0) {
        const firstLabel = axisLabels[0].textContent.trim();
        if (firstLabel && firstLabel !== '0') {
          return `${firstLabel} Chart`;
        }
      }
    }
    
    // Fallback to generic title
    return `Chart ${index + 1}`;
  }

  // Extract findings text from the rendered DOM
  async extractFindingsFromDOM(tabName) {
    try {
      
      // First, ensure we're on the correct tab
      this.switchToTab(tabName);
      
      // Wait a moment for the content to render
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Find the first card in the tab - look for Card or Paper with the tab title
      // The tab title is typically in an h5 or h2 with the tab name
      const tabCards = Array.from(document.querySelectorAll('.MuiCard-root, .MuiPaper-root'));
      let firstCard = null;
      
      // Look for the card that contains the tab name in its title
      for (const card of tabCards) {
        const titleElement = card.querySelector('h5, h2, .MuiTypography-h5, .MuiTypography-h2');
        if (titleElement && titleElement.textContent.trim() === tabName) {
          firstCard = card;
          break;
        }
      }
      
      // Fallback: if not found, use the first card/paper in the tab
      if (!firstCard && tabCards.length > 0) {
        firstCard = tabCards[0];
      }

      if (!firstCard) {
        return '';
      }

      // Find all Typography variant="body1" elements (they render as <p> tags)
      // We want the ones that come after "Unique Operator Names — X"
      const allParagraphs = Array.from(firstCard.querySelectorAll('p'));
      let findingsText = '';
      let startCollecting = false;
      
      for (const paragraph of allParagraphs) {
        const text = paragraph.textContent.trim();
        
        // Skip empty text
        if (!text) {
          continue;
        }
        
        // Start collecting after we see "Unique Operator Names"
        if (text.includes('Unique Operator Names')) {
          startCollecting = true;
          continue;
        }
        
        // Collect all text after the operator names line
        if (startCollecting) {
          findingsText += text + '\n';
        }
      }
      
      return findingsText.trim();
    } catch (error) {
      return '';
    }
  }

  // Add Key Findings section
  async addKeyFindings(tabName, data, yPosition) {
    if (!this.doc) return yPosition + 1;

    this.addSectionTitle('Findings', yPosition);
    
    let currentY = yPosition + 0.3;
    
    // Extract findings text from the rendered DOM instead of generating
    const findingsText = await this.extractFindingsFromDOM(tabName);
    
    if (findingsText && findingsText.trim().length > 0) {
      // Each line from findingsText is already a paragraph from <p> tags
      const paragraphs = findingsText.split('\n').filter(p => p.trim().length > 0);
      
      
      paragraphs.forEach((paragraph) => {
        if (currentY > this.pageHeight - 1) {
          this.addNewPage();
          currentY = this.margin;
        }
        
        // Text is already clean from <p> tag extraction
        if (paragraph.trim().length > 0) {
          // Use addText with proper margins and wrapping
          this.addText(paragraph, this.margin + 0.2, currentY, {
            maxWidth: this.contentWidth - 0.4, // Add margins on both sides
            fontSize: 10,
            fontStyle: 'normal'
          });
          
          // Calculate the actual height of the text that was rendered
          const textHeight = this.calculateTextHeight(paragraph, this.contentWidth - 0.4, 10);
          // Add spacing equivalent to 1 line of text (convert points to inches)
          const lineSpacing = (10 / 72) * 1.2; // 1 line worth of spacing
          
          
          currentY += textHeight + lineSpacing;
        }
      });
    }

    // Also capture tables from the Findings section
    currentY = await this.addFindingsTables(tabName, currentY);

    return currentY + 0.2;
  }

  // Add tables from Findings sections
  async addFindingsTables(tabName, yPosition) {
    if (!this.doc) return yPosition + 1;

    let currentY = yPosition;
    
    try {
      // Find all table containers with data-table-type="findings"
      const findingsTables = document.querySelectorAll('[data-table-type="findings"]');
      
      if (findingsTables.length > 0) {
        for (let i = 0; i < findingsTables.length; i++) {
          const tableContainer = findingsTables[i];
          const tableTitle = tableContainer.getAttribute('data-table-title');
          
          // Find the actual table element within the container
          const tableElement = tableContainer.querySelector('table');
          
          if (tableElement && tableTitle) {
            // Check if we need a new page for this table
            if (currentY > this.pageHeight - 2) {
              this.addNewPage();
              currentY = 1.2;
            }
            
            // Add table to current page (mark as Findings table)
            currentY = await this.addTableFromElement(tableElement, tableTitle, currentY, true);
          }
        }
      }
    } catch (error) {
      // Handle error silently
    }

    return currentY;
  }

  // Cancel PDF generation
  cancelGeneration() {
    this.isCancelled = true;
  }

  // Generate and download PDF
  async generateReport(searchData, chartElements = {}, progressCallback = null) {
    try {
      this.isCancelled = false; // Reset cancellation flag
      this.initDocument();
      
      // Add title page
      await this.addTitlePage(searchData);
      
      // Add Report Contents page
      if (this.isCancelled) {
        throw new Error('PDF generation cancelled');
      }
      if (progressCallback) {
        progressCallback('Creating Report Contents...');
      }
      await this.addReportContentsPage(searchData);

      // Chapters / empty gates follow filtered data when map filter is active (matches on-screen tabs)
      const chapterData = this.getChapterSearchData(searchData);
      const availableTabs = this.getAvailableTabs(chapterData);
      
        // Add Report Card Analysis page (only if Wells was searched and still has data for chapters)
        if (
          this.wasDataTypeSearched('Wells', searchData) &&
          this.getTableRecordCount(chapterData, 'Wells') > 0
        ) {
          if (this.isCancelled) {
            throw new Error('PDF generation cancelled');
          }
          if (progressCallback) {
            progressCallback('Processing Report Card Analysis...');
          }
          this.addNewPage();
          const reportCardPageNumber = this.getCurrentPageNumber();
          await this.addPageHeader('Report Card Analysis', reportCardPageNumber);
          await this.addReportCardAnalysisPage(chapterData);
        }
        
        // Add content pages for each tab
      for (let i = 0; i < availableTabs.length; i++) {
        const tabName = availableTabs[i];
        
        if (this.isCancelled) {
          throw new Error('PDF generation cancelled');
        }
        
        if (progressCallback) {
          progressCallback(`Processing ${tabName}...`);
        }
        
        this.addNewPage();
        const pageNumber = this.getCurrentPageNumber();
        const chapterTitle = searchData?.filter?.isActive
          ? `${tabName} Analytics (Filtered)`
          : `${tabName} Analytics`;
        await this.addPageHeader(chapterTitle, pageNumber);
        
        let currentY = 1.2;
        
        // Add tab-specific content (gated on chapterData)
        currentY = await this.addTabContent(tabName, chapterData, currentY, chartElements);
      }

      // Save the PDF
      if (this.isCancelled) {
        throw new Error('PDF generation cancelled');
      }
      if (progressCallback) progressCallback('Finalizing PDF report...');
      const fileName = `Diligence_Desk_Analytics_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      this.doc.save(fileName);

      return { success: true, fileName };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Add content for specific tab
  async addTabContent(tabName, searchData, startY, chartElements) {
    // Map tab names to search result keys
    const tabKeyMap = {
      'Wells': 'wells',
      'Production': 'production', 
      'Inspections': 'inspections',
      'Violations': 'violations',
      'Severances': 'severances',
      'Spills': 'spills'
    };
    
    const tabKey = tabKeyMap[tabName] || tabName.toLowerCase();
    const data = searchData?.data?.[tabKey] || [];
    
    if (!data || data.length === 0) {
      this.addText(`No data available for ${tabName} category.`, 0.6, startY);
      return startY + 0.5;
    }

    let currentY = startY;

    // Add Findings section
    currentY = await this.addKeyFindings(tabName, data, currentY);

    // Add charts with pagination (2 charts per page, or start on page 2 if Findings tables exist)
    currentY = await this.addChartsWithPagination(tabName, chartElements, currentY, searchData);

    // Add tables on separate pages
    await this.addTablesOnSeparatePages(tabName, searchData);

    return currentY;
  }

  // Add charts with pagination (2 charts per page)
  async addChartsWithPagination(tabName, chartElements, startY, searchData) {
    if (!this.doc) return startY + 1;

    let currentY = startY;
    
    try {
      // First, try to switch to the correct tab to capture its charts
      await this.switchToTab(tabName);
      
      // Wait for charts to render (smart waiting, max 3 seconds)
      await this.waitForChartsToRender();
      
      // Find all ResponsiveContainer elements (Recharts containers) in the current tab
      const chartContainers = document.querySelectorAll('.recharts-wrapper');
      
      if (chartContainers.length > 0) {
        // Check if there are Findings tables - if so, start charts on page 2
        const findingsTables = document.querySelectorAll('[data-table-type="findings"]');
        const chartsPerPage = 2; // Always 2 charts per page
        const totalCharts = chartContainers.length;
        const totalPages = Math.ceil(totalCharts / chartsPerPage);
        const startOnPage2 = findingsTables.length > 0;
        
        for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
          // If this is not the first page OR if we need to start on page 2, create a new page
          if (pageIndex > 0 || startOnPage2) {
            this.addNewPage();
            // Get page number after adding the page
            const pageNumber = this.getCurrentPageNumber();
            await this.addPageHeader(`${tabName} Analytics - Continued`, pageNumber);
            currentY = 1.2;
          }
          
          // Add charts for this page (up to chartsPerPage)
          const startChartIndex = pageIndex * chartsPerPage;
          const endChartIndex = Math.min(startChartIndex + chartsPerPage, totalCharts);
          
          for (let i = startChartIndex; i < endChartIndex; i++) {
            const chartElement = chartContainers[i];
            const chartTitle = this.getChartTitleForIndex(tabName, i);
            
            currentY = await this.addChartFromElement(chartElement, chartTitle, currentY);
          }
          
          // Add footer to this page
          this.addFooter(searchData);
        }
      } else {
        // No charts found - skip this section
      }
    } catch (error) {
      // No fallback - charts must be captured from DOM
    }

    return currentY;
  }

  // Add tables on separate pages (excluding Findings tables which are already captured)
  async addTablesOnSeparatePages(tabName, searchData) {
    if (!this.doc) return;

    try {
      // Switch to the correct tab to capture its tables
      await this.switchToTab(tabName);
      
      // Wait for tables to render (smart waiting, max 1 second)
      await this.waitForTablesToRender();
      
      // Find all table containers with data-table-title attributes in the current tab
      const tableContainers = document.querySelectorAll('[data-table-title]');
      
      // Filter out Findings tables (these are already captured in addKeyFindings)
      // Only include tables with data-table-type="main"
      const mainTables = Array.from(tableContainers).filter(container => {
        const tableType = container.getAttribute('data-table-type');
        return tableType === 'main';
      });
      
      if (mainTables.length > 0) {
        for (let i = 0; i < mainTables.length; i++) {
          const tableContainer = mainTables[i];
          const tableTitle = tableContainer.getAttribute('data-table-title');
          
          // Find the actual table element within the container
          const tableElement = tableContainer.querySelector('table');
          
          if (tableElement && tableTitle) {
            // Create new page for each table
            this.addNewPage();
            // Get page number after adding the page
            const pageNumber = this.getCurrentPageNumber();
            await this.addPageHeader(`${tabName} Analytics - Continued`, pageNumber);
            
            // Add table to page
            await this.addTableFromElement(tableElement, tableTitle, 1.2);
            
            // Add footer to this page
            this.addFooter(searchData);
          }
        }
      }
    } catch (error) {
      // Handle error silently
    }
  }

  // Get table title for index by extracting from DOM data attributes
  getTableTitleForIndex(tabName, index) {
    try {
      // Find all table containers with data-table-title attributes in the current tab
      const tableContainers = document.querySelectorAll('[data-table-title]');
      
      if (tableContainers.length > index) {
        const tableElement = tableContainers[index];
        const title = tableElement.getAttribute('data-table-title');
        if (title && title.trim().length > 0) {
          return title.trim();
        }
      }
    } catch (error) {
      // Handle error silently
    }
    
    // Fallback to generic title
    return `Table ${index + 1}`;
  }

  // Add Findings table title (styled like subsection within Findings)
  addFindingsTableTitle(title, yPosition) {
    if (!this.doc) return yPosition + 0.3;

    // Add more space before the title to separate it from text above
    const titleY = yPosition + 0.4;

    // Set text color to grayscale (dark gray)
    this.doc.setTextColor(100, 100, 100);
    
    // Add title without underline, smaller font
    this.doc.setFontSize(11);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(title, this.margin + 0.2, titleY);
    
    // Reset text color to black for subsequent content
    this.doc.setTextColor(0, 0, 0);
    
    // Return position closer to the table (less space after title)
    return titleY + 0.2;
  }

  // Add table from DOM element with pagination
  async addTableFromElement(tableElement, title, yPosition, isFindingsTable = false) {
    if (!this.doc) return yPosition + 2;

    let adjustedYPosition = yPosition;
    
    if (isFindingsTable) {
      // Add Findings table title without underline, in grayscale
      adjustedYPosition = this.addFindingsTableTitle(title, yPosition);
    } else {
      this.addSectionTitle(title, yPosition);
    }
    
    try {
      if (tableElement) {
        // Temporarily expand table width for better capture
        let originalStyles = await this.temporarilyExpandTableWidth(tableElement);
        
        // Wait for table to re-render with expanded width
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Validate table is properly expanded, retry if not
        let retryCount = 0;
        const maxRetries = 3;
        while (retryCount < maxRetries && !this.isTableProperlyExpanded(tableElement)) {
          // Restore and try again
          this.restoreTableWidth(tableElement, originalStyles);
          await new Promise(resolve => setTimeout(resolve, 300));
          
          // Re-expand
          const newOriginalStyles = this.temporarilyExpandTableWidth(tableElement);
          await new Promise(resolve => setTimeout(resolve, 500));
          
          retryCount++;
          if (retryCount < maxRetries) {
            originalStyles = newOriginalStyles;
          }
        }
        
        // Capture the full table as an image
        const fullCanvas = await html2canvas(tableElement, {
          backgroundColor: '#ffffff',
          scale: 1, // Normal resolution for better performance
          useCORS: true,
          allowTaint: true
        });
        
        // Restore original styles
        this.restoreTableWidth(tableElement, originalStyles);
        
        // Use fixed width table approach (simplified)
        return await this.addFixedWidthTable(fullCanvas, title, adjustedYPosition, isFindingsTable);
      }
    } catch (error) {
      // Handle error silently
    }
    
    return yPosition + 2;
  }

  // Add fixed width table with proper cropping
  async addFixedWidthTable(canvas, title, yPosition, isFindingsTable = false) {
    if (!this.doc) return yPosition + 2;

    // Fixed table width (7 inches)
    const fixedWidth = 7.0;
    const xPosition = (this.pageWidth - fixedWidth) / 2;
    
    // Calculate natural height based on fixed width
    const tableAspectRatio = canvas.width / canvas.height;
    const naturalHeight = fixedWidth / tableAspectRatio;
    
    // Calculate available height on current page
    const availableHeight = this.pageHeight - yPosition - 0.5; // Leave 0.5" margin at bottom
    
    // Determine how much of the table we can show on this page
    const maxHeightOnPage = Math.min(availableHeight, 8.75);
    
    if (naturalHeight <= maxHeightOnPage) {
      // Table fits on current page - show it all
      const imgData = canvas.toDataURL('image/jpeg', 0.8);
      this.doc.addImage(imgData, 'JPEG', xPosition, yPosition + 0.1, fixedWidth, naturalHeight);
      return yPosition + naturalHeight + 0.5;
    } else {
      // Table is too tall - crop it and show what fits
      const cropRatio = maxHeightOnPage / naturalHeight;
      const croppedHeight = maxHeightOnPage;
      
      // Create a cropped canvas showing only the top portion
      const croppedCanvas = await this.cropCanvas(canvas, 0, 0, canvas.width, canvas.height * cropRatio);
      const imgData = croppedCanvas.toDataURL('image/jpeg', 0.8);
      
      // Add the cropped table to current page
      this.doc.addImage(imgData, 'JPEG', xPosition, yPosition + 0.1, fixedWidth, croppedHeight);
      
      // Add continuation note
      const noteY = yPosition + croppedHeight + 0.2;
      this.doc.setFontSize(8);
      this.doc.setTextColor(150, 150, 150);
      this.doc.text('(Table continues on next page)', xPosition, noteY);
      
      // Move to next page and continue with remaining portion
      this.addNewPage();
      const nextPageY = this.margin;
      
      // Add "continued" header
      this.doc.setFontSize(10);
      this.doc.setTextColor(100, 100, 100);
      this.doc.text(`${title} (continued)`, this.margin, nextPageY);
      
      // Recursively add the remaining portion
      const remainingCanvas = await this.cropCanvas(canvas, 0, canvas.height * cropRatio, canvas.width, canvas.height * (1 - cropRatio));
      return await this.addFixedWidthTable(remainingCanvas, title, nextPageY + 0.3, isFindingsTable);
    }
  }

  // Helper method to crop a canvas
  async cropCanvas(sourceCanvas, x, y, width, height) {
    const croppedCanvas = document.createElement('canvas');
    const ctx = croppedCanvas.getContext('2d');
    
    croppedCanvas.width = width;
    croppedCanvas.height = height;
    
    ctx.drawImage(sourceCanvas, x, y, width, height, 0, 0, width, height);
    
    return croppedCanvas;
  }

  // Temporarily expand table width for better capture
  async temporarilyExpandTableWidth(tableElement) {
    const originalStyles = {
      width: tableElement.style.width,
      minWidth: tableElement.style.minWidth,
      maxWidth: tableElement.style.maxWidth
    };
    
    // Expand table width for better mobile capture
    tableElement.style.width = '800px';
    tableElement.style.minWidth = '800px';
    tableElement.style.maxWidth = '800px';
    
    return originalStyles;
  }

  // Restore table width
  restoreTableWidth(tableElement, originalStyles) {
    tableElement.style.width = originalStyles.width;
    tableElement.style.minWidth = originalStyles.minWidth;
    tableElement.style.maxWidth = originalStyles.maxWidth;
  }

  // Get current page number
  getCurrentPageNumber() {
    return this.doc.internal.getCurrentPageInfo().pageNumber;
  }

  // Smart waiting for charts to render (max 3 seconds)
  async waitForChartsToRender() {
    return new Promise((resolve) => {
      const startTime = Date.now();
      const maxWaitTime = 3000; // 3 seconds max
      
      const checkCharts = () => {
        const elapsed = Date.now() - startTime;
        
        // Timeout after 3 seconds
        if (elapsed >= maxWaitTime) {
          resolve();
          return;
        }
        
        // Check if all charts in current tab are fully rendered
        const charts = document.querySelectorAll('.recharts-responsive-container');
        let readyCount = 0;
        
        charts.forEach(chart => {
          // Check if chart has proper dimensions and content
          if (chart.offsetWidth > 0 && chart.offsetHeight > 0) {
            // Check if chart content is rendered (not just container)
            const chartContent = chart.querySelector('.recharts-wrapper');
            if (chartContent && chartContent.offsetWidth > 0) {
              // Check if chart has actual data rendered
              const chartElements = chart.querySelectorAll('.recharts-bar, .recharts-line, .recharts-area');
              if (chartElements.length > 0) {
                readyCount++;
              }
            }
          }
        });
        
        if (readyCount === charts.length && charts.length > 0) {
          resolve(); // All charts ready!
        } else {
          setTimeout(checkCharts, 100); // Check again in 100ms
        }
      };
      
      checkCharts();
    });
  }

  // Smart waiting for tables to render (max 1 second)
  async waitForTablesToRender() {
    return new Promise((resolve) => {
      const startTime = Date.now();
      const maxWaitTime = 1000; // 1 second max
      
      const checkTables = () => {
        const elapsed = Date.now() - startTime;
        
        // Timeout after 1 second
        if (elapsed >= maxWaitTime) {
          resolve();
          return;
        }
        
        // Check if all tables in current tab are fully rendered
        const tables = document.querySelectorAll('[data-table-title]');
        let readyCount = 0;
        
        tables.forEach(table => {
          // Check if table has proper dimensions and content
          if (table.offsetWidth > 0 && table.offsetHeight > 0) {
            // Check if table has actual rows rendered
            const tableRows = table.querySelectorAll('tbody tr, tbody td');
            if (tableRows.length > 0) {
              readyCount++;
            }
          }
        });
        
        if (readyCount === tables.length && tables.length > 0) {
          resolve(); // All tables ready!
        } else {
          setTimeout(checkTables, 50); // Check again in 50ms (faster for tables)
        }
      };
      
      checkTables();
    });
  }

  // Validate if chart is properly expanded (width should be close to 800px)
  isChartProperlyExpanded(chartElement) {
    if (!chartElement) return false;
    
    const rect = chartElement.getBoundingClientRect();
    const expectedWidth = 800;
    const tolerance = 50; // Allow 50px tolerance
    
    return rect.width >= (expectedWidth - tolerance) && rect.width <= (expectedWidth + tolerance);
  }

  // Validate if table is properly expanded (width should be close to 800px)
  isTableProperlyExpanded(tableElement) {
    if (!tableElement) return false;
    
    const rect = tableElement.getBoundingClientRect();
    const expectedWidth = 800;
    const tolerance = 50; // Allow 50px tolerance
    
    return rect.width >= (expectedWidth - tolerance) && rect.width <= (expectedWidth + tolerance);
  }

  // Retry chart expansion and capture with validation
  async captureChartWithRetry(chartElement, title, yPosition, maxRetries = 3) {
    if (!this.doc) return yPosition + 2;

    this.addSectionTitle(title, yPosition);
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        if (chartElement) {
          // Temporarily expand chart width for better mobile capture
          const originalStyles = await this.temporarilyExpandChartWidth(chartElement);
          
          // Wait for chart to re-render with expanded width
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Validate chart is properly expanded
          if (!this.isChartProperlyExpanded(chartElement)) {
            if (attempt < maxRetries) {
              // Restore and try again
              this.restoreChartWidth(chartElement, originalStyles);
              await new Promise(resolve => setTimeout(resolve, 500));
              continue;
            }
          }
          
          // Capture the chart as an image
          const canvas = await html2canvas(chartElement, {
            backgroundColor: null,
            scale: 1,
            useCORS: true,
            allowTaint: true
          });
          
          // Restore original styles
          this.restoreChartWidth(chartElement, originalStyles);
          
          const imgData = canvas.toDataURL('image/jpeg', 0.8);
          const imgWidth = this.contentWidth - 0.4;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          
          // Limit height to prevent overflow
          const maxHeight = 3.0;
          let finalWidth = imgWidth;
          let finalHeight = imgHeight;
          
          if (finalHeight > maxHeight) {
            finalHeight = maxHeight;
            finalWidth = (canvas.width * finalHeight) / canvas.height;
          }
          
          const xPosition = (this.pageWidth - finalWidth) / 2;
          this.doc.addImage(imgData, 'JPEG', xPosition, yPosition + 0.3, finalWidth, finalHeight);
          
          return yPosition + finalHeight + 0.5;
        }
      } catch (error) {
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 500));
          continue;
        }
      }
    }
    
    return yPosition + 2;
  }

  // Retry table expansion and capture with validation
  async captureTableWithRetry(tableElement, title, yPosition, maxRetries = 3) {
    if (!this.doc) return yPosition + 2;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        if (tableElement) {
          // Temporarily expand table width for better mobile capture
          const originalStyles = this.temporarilyExpandTableWidth(tableElement);
          
          // Wait for table to re-render with expanded width
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Validate table is properly expanded
          if (!this.isTableProperlyExpanded(tableElement)) {
            if (attempt < maxRetries) {
              // Restore and try again
              this.restoreTableWidth(tableElement, originalStyles);
              await new Promise(resolve => setTimeout(resolve, 300));
              continue;
            }
          }
          
          // Capture the table as an image
          const canvas = await html2canvas(tableElement, {
            backgroundColor: null,
            scale: 1,
            useCORS: true,
            allowTaint: true
          });
          
          // Restore original styles
          this.restoreTableWidth(tableElement, originalStyles);
          
          return await this.addFixedWidthTable(canvas, title, yPosition);
        }
      } catch (error) {
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 300));
          continue;
        }
      }
    }
    
    return yPosition + 2;
  }

}

const pdfReportService = new PDFReportService();
export default pdfReportService;
